import { useDispatch } from "react-redux";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { useMount } from "app/custom-hooks/useMount";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import {
    BaseActivatedPanel,
    StyledInnerContainer,
    StyledPanelIcon,
    type BaseActivatedPanelProps,
    SelectionDDItem,
    SelectionDropdownFooter,
    useActionsPanelState,
    ManualSelectionItem,
    type SelectionDropdown,
    type SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";

import { setIsExcelExportActive } from "../../store/action-creators";

import { StyledInfoMessage, StyledSelectionDropdown, StyledSubmitButton, StyledTitle } from "./styles";

type LegacyWebsitesExcelExportPanelViewProps = BaseActivatedPanelProps & {
    isDisabled: boolean;
    isLoading: boolean;
    numberOfSelectedItems: number;
    numberOfRemainingExports: number;
    numberOfResults: number;
    numberOfTableColumns?: number;
    selectionDropdown: SelectionDropdown;
    onSelectionChange(item: Readonly<SelectionDropdownItem>): void;
    onSubmit(activeSelectionItem: Readonly<SelectionDropdownItem>): void;
};

/* TODO move to a shared component */
const LegacyWebsitesExcelExportPanelView = (props: LegacyWebsitesExcelExportPanelViewProps) => {
    const dispatch = useDispatch();
    const {
        isDisabled,
        isLoading,
        numberOfRemainingExports,
        numberOfResults,
        numberOfSelectedItems,
        numberOfTableColumns = 0,
        selectionDropdown,
        onSelectionChange,
        onSubmit,
    } = props;
    const state = useActionsPanelState(selectionDropdown, {
        numberOfResults,
        numberOfSelectedItems,
        onSelectionChange,
        limit: numberOfRemainingExports,
    });
    const isSubmitDisabled = isDisabled || state.isSelectionEmpty;

    useMount(() => {
        dispatch(setIsExcelExportActive(true));
        return () => {
            dispatch(setIsExcelExportActive(false));
        };
    });

    useDidUpdate(() => {
        if (!isLoading) {
            onSelectionChange(state.activeSelectionItem);
        }
    }, [isLoading]);

    function handleSubmit() {
        onSubmit(state.activeSelectionItem);
    }

    return (
        <BaseActivatedPanel onDeactivate={props.onDeactivate}>
            <StyledInnerContainer>
                <StyledPanelIcon
                    iconName="excel"
                    size="xs"
                />
                <I18n
                    data-automation="actions-panel.export-label"
                    element={StyledTitle}
                    translatable={{ key: "si.multi_selector.excel.title" }}
                />
                <StyledSelectionDropdown
                    buttonText={state.activeSelectionItem.getSelectedTranslation()}
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                    footer={
                        <SelectionDropdownFooter
                            numberOfRemainingItems={numberOfRemainingExports}
                            label={translate("si.multi_selector.excel.dropdown.bottom.label")}
                        />
                    }
                    onSelect={state.setActiveSelectionItem}
                >
                    {state.dropdownItems
                        .map((item) => (
                            <SelectionDDItem
                                key={item.id}
                                item={item}
                                isSelected={state.activeSelectionItem.id === item.id}
                            />
                        ))
                        .concat(
                            <SelectionDDItem
                                item={state.manualSelectionItem}
                                key={ManualSelectionItem.ID}
                                isSelected={state.activeSelectionItem.id === ManualSelectionItem.ID}
                            />,
                        )}
                </StyledSelectionDropdown>
                <StyledSubmitButton
                    isDisabled={isSubmitDisabled}
                    isLoading={isLoading}
                    text={translate("si.common.button.export")}
                    tooltipContent={translate("si.multi_selector.tooltip.excel.unselected_domains")}
                    onClick={handleSubmit}
                />
                {numberOfTableColumns > 0 ? (
                    <StyledInfoMessage>
                        {translate("si.multi_selector.excel.columns_info", {
                            numberOfColumns: numberOfTableColumns,
                        })}
                    </StyledInfoMessage>
                ) : null}
            </StyledInnerContainer>
        </BaseActivatedPanel>
    );
};

export default LegacyWebsitesExcelExportPanelView;
